"use client";

import { memo, useState, useEffect, useRef, useTransition } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { FeedCard, FeedCardSkeleton } from "./feed-card";
import { LibraryCard } from "./library/LibraryCard";
import { LibraryVideoModal } from "./library/LibraryVideoModal";
import type { LibraryItem } from "@/lib/mock/library";
import { LIBRARY_VIDEOS } from "@/lib/videos-catalog";
import { skillToLibraryItem } from "@/lib/skills-map";
import { signalToLibraryItem } from "@/lib/signals-map";
import { FeedHeader, type ShowcaseTab } from "./feed-header";
import {
  apiClient,
  type FeedSession,
  type CuratedSession,
  type FeaturedAlertItem,
} from "@/lib/api-client";
import { usageService } from "@/services/usageService";
import { ArrowRight, BookOpen, TrendingUp, Activity, LineChart, ArrowLeftRight, BarChart3, Search, GraduationCap, GitFork, RefreshCw, FileText, Repeat } from "lucide-react";
import Link from "next/link";
import { Button } from "./ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import {
  CuratedStrategy,
  getCuratedStrategies,
} from "@/lib/api/mock-curated-strategies";
import { CuratedCard } from "./curated/CuratedCard";
import { CaseStudyCard } from "./case-studies/CaseStudyCard";
import { CASE_STUDIES } from "@/utils/caseStudiesCatalog";
import {
  AutopilotCreatorCard,
  AutopilotCreatorCardSkeleton,
} from "./autopilot/AutopilotCreatorCard";
import { createChannelLogger } from "@/lib/debug-log";
import { buildLibraryBrowseMoreHref } from "@/lib/library-routes";
import {
  buildCommunityTourUrl,
  HOME_SHOWCASE_TOUR_TAB_EVENT,
  getHomeShowcaseStage,
  isCommunityTourVisit,
} from "@/lib/onboarding-constants";

// Autopilot featured-alerts row — dedicated channel so we can watch the
// fetch lifecycle end-to-end without mixing with other frontend logs.
// Writes to `frontend_autopilot.txt` at the repo root (via /api/debug-log).
const autopilotLog = createChannelLogger("autopilot");

const CURATED_DIFFICULTIES: { key: string | null; label: string; icon?: React.ComponentType<{ className?: string }> }[] = [
  { key: null, label: "All Levels" },
  { key: "beginner", label: "Beginner", icon: BookOpen },
  { key: "intermediate", label: "Intermediate", icon: TrendingUp },
  { key: "advanced", label: "Advanced", icon: Activity },
];

const CURATED_THEMES: { key: string | null; label: string; icon?: React.ComponentType<{ className?: string }> }[] = [
  { key: null, label: "All Themes" },
  { key: "options", label: "Options", icon: ArrowLeftRight },
  { key: "stocks", label: "Stocks", icon: LineChart },
  { key: "screener", label: "Screener", icon: Search },
  { key: "research", label: "Research", icon: BarChart3 },
  { key: "fundamentals", label: "Fundamentals", icon: GraduationCap },
  { key: "education", label: "Education", icon: BookOpen },
  { key: "backtest", label: "Backtest", icon: Repeat },
  { key: "swing_trade", label: "Swing Trade", icon: TrendingUp },
  { key: "sec_filings", label: "SEC Filings", icon: FileText },
  { key: "sub_agents", label: "Parallel Agents", icon: GitFork },
  { key: "rerunnable", label: "Rerunnable", icon: RefreshCw },
];

const THEME_LABELS: Record<string, string> = Object.fromEntries(
  CURATED_THEMES.filter((t) => t.key).map((t) => [t.key!, t.label])
);

interface FeedProps {
  limit?: number;
  minScore?: number;
  variant?: "teaser" | "full";
  stickyHeader?: boolean;
  showCuratedStrategies?: boolean;
}

function PureFeed({
  limit = 6,
  minScore = 5.0,
  variant = "teaser",
  stickyHeader = false,
  showCuratedStrategies = true,
}: FeedProps) {
  const { user } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [skillItems, setSkillItems] = useState<LibraryItem[]>([]);
  const [signalItems, setSignalItems] = useState<LibraryItem[]>([]);
  // Community-tour mode: active when the URL carries
  // `?onboarding_community=1`. Drives the tour-aware FeedCard wiring
  // (first card gets a `data-tour` anchor + all cards swap their href
  // to the shared-chat tour URL) so CommunityBuddyTour's click_target
  // beat lands on a real anchor and hands off to CommunityChatBuddyTour.
  const communityTourActive = isCommunityTourVisit(searchParams);
  const [sessions, setSessions] = useState<FeedSession[]>([]);
  const [curatedStrategies, setCuratedStrategies] = useState<CuratedStrategy[]>(
    []
  );
  const [curatedSessions, setCuratedSessions] = useState<CuratedSession[]>([]);
  const [curatedCount, setCuratedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ShowcaseTab>("automations");
  const [activeVideo, setActiveVideo] = useState<LibraryItem | null>(null);
  const [sortBy, setSortBy] = useState<"relevance" | "recent">("relevance");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string | null>(null);
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [fetchLimit, setFetchLimit] = useState<number>(limit);
  const [total, setTotal] = useState<number | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isCuratedFetching, setIsCuratedFetching] = useState(variant === "teaser");
  const [hasLoadedCuratedSessions, setHasLoadedCuratedSessions] = useState(variant !== "teaser");
  const [hasMore, setHasMore] = useState(false);
  const [totalUsers, setTotalUsers] = useState<number | null>(null);
  // Featured (Autopilot) alerts row — lazy-fetched the first time the
  // "automations" tab is visible so we don't pay the network cost on
  // home-page first paint if the user is browsing "Top Trade Ideas".
  //
  // IMPORTANT: the "has fired" guard lives in a ref, NOT state. If we
  // put `featuredLoading` / `featuredLoaded` in the effect's deps, any
  // setState inside the effect (setFeaturedLoading(true)) triggers a
  // re-run of the effect, which runs the cleanup of the PREVIOUS run,
  // which sets `cancelled=true` on the in-flight fetch's closure, which
  // causes the fetch's `.then()` to swallow the state update. The net
  // effect is cards never render even though the HTTP call succeeded.
  // Using a ref keeps the idempotency guard OUT of the deps array.
  const [featuredAlerts, setFeaturedAlerts] = useState<FeaturedAlertItem[]>([]);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [featuredLoaded, setFeaturedLoaded] = useState(false);
  const [tourActiveTab, setTourActiveTab] = useState<ShowcaseTab | null>(null);
  const featuredFiredRef = useRef(false);
  const [, startTransition] = useTransition();

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Pre-seed `tourActiveTab` from the URL stage so the Browse more
    // link points at the right downstream tour URL even before the
    // first tab event fires. We deliberately DO NOT pre-seed
    // `activeTab` for trade_ideas / community: the user is meant to
    // click the corresponding pill themselves as the first beat of
    // those stages, so the home should load on its default automations
    // tab and let the click do the flip.
    const params = new URLSearchParams(window.location.search);
    const stage = getHomeShowcaseStage(params);
    if (stage === "automations") {
      setTourActiveTab("automations");
      setActiveTab("automations");
    } else if (stage === "trade_ideas") {
      setTourActiveTab("curated");
    } else if (stage === "community") {
      setTourActiveTab("community");
    }

    const handleTourTab = (event: Event) => {
      const tab =
        (event as CustomEvent<{ tab?: ShowcaseTab | null }>).detail?.tab ?? null;
      // Ignore null tab events so the resume-stage pre-seed of
      // `tourActiveTab` survives. HomeShowcaseBuddyTour emits null
      // when a stage starts on a beat that doesn't want to force
      // any tab change (e.g. the trade_ideas pill_click beat).
      if (tab === null) return;
      setTourActiveTab(tab);
      setActiveTab(tab);
    };

    window.addEventListener(HOME_SHOWCASE_TOUR_TAB_EVENT, handleTourTab);
    return () =>
      window.removeEventListener(HOME_SHOWCASE_TOUR_TAB_EVENT, handleTourTab);
  }, []);

  useEffect(() => {
    const fetchTotalUsers = async () => {
      try {
        const data = await usageService.getTotalUsers();
        setTotalUsers(data.total_users);
      } catch (err) {
      }
    };
    fetchTotalUsers();
  }, []);

  // Skills + Signals for the home showcase tabs (teaser only).
  useEffect(() => {
    if (variant !== "teaser") return;
    apiClient
      .listPublicSkills({ limit: 24 })
      .then((res) => setSkillItems((res.skills || []).map(skillToLibraryItem)))
      .catch(() => {});
    apiClient
      .listPublicSignals({ limit: 24 })
      .then((res) =>
        setSignalItems((res.signals || []).map(signalToLibraryItem)),
      )
      .catch(() => {});
  }, [variant]);

  // Lazy-load the featured Autopilot alerts once, the first time the
  // "automations" tab becomes active. `featuredFiredRef` is the one-shot
  // guard (a ref so it doesn't live in the deps array — see the comment
  // next to its declaration above for why that matters).
  //
  // Deps deliberately ONLY include `variant` and `activeTab`. That way
  // setState calls inside the effect don't cause the cleanup to fire
  // mid-fetch and swallow the response.
  useEffect(() => {
    autopilotLog("effect:fire", {
      variant,
      activeTab,
      alreadyFired: featuredFiredRef.current,
    });
    if (variant !== "teaser") {
      autopilotLog("effect:skip", { reason: "variant_not_teaser", variant });
      return;
    }
    if (activeTab !== "automations") {
      autopilotLog("effect:skip", { reason: "tab_not_automations", activeTab });
      return;
    }
    if (featuredFiredRef.current) {
      autopilotLog("effect:skip", { reason: "already_fired_once" });
      return;
    }
    featuredFiredRef.current = true;

    setFeaturedLoading(true);
    autopilotLog("fetch:start", { url: "/alerts/featured?limit=24" });
    const t0 = Date.now();
    apiClient
      .listFeaturedAlerts({ limit: 24 })
      .then((resp) => {
        const elapsed = Date.now() - t0;
        autopilotLog("fetch:ok", {
          elapsed_ms: elapsed,
          count: resp?.alerts?.length ?? 0,
          first_alert_id: resp?.alerts?.[0]?.alert_id,
          first_thumb: resp?.alerts?.[0]?.thumbnail_url,
          first_handle: resp?.alerts?.[0]?.presented_by?.handle,
        });
        setFeaturedAlerts(resp.alerts || []);
        setFeaturedLoaded(true);
        autopilotLog("state:set_alerts", {
          newCount: (resp.alerts || []).length,
        });
      })
      .catch((err) => {
        const elapsed = Date.now() - t0;
        autopilotLog("fetch:error", {
          elapsed_ms: elapsed,
          message: String(err?.message || err),
        });
        console.warn("[Feed] featured alerts fetch failed:", err);
        // Allow a retry on the next tab switch if the first attempt failed.
        featuredFiredRef.current = false;
        setFeaturedLoaded(true);
      })
      .finally(() => {
        setFeaturedLoading(false);
        autopilotLog("state:loading_false", {});
      });
    // NOTE: no cleanup returned. The fetch is fire-and-forget — if the
    // component unmounts mid-flight, the setState calls land on a dead
    // tree which React handles gracefully (no memory leak, no warning
    // in React 18+). We accept that over the alternative of the effect
    // re-running and killing its own in-flight fetch.
  }, [variant, activeTab]);

  useEffect(() => {
    // Always fetch Curated Strategies
    if (showCuratedStrategies && curatedStrategies.length === 0) {
      const fetchCurated = async () => {
        try {
          const strategies = await getCuratedStrategies();
          setCuratedStrategies(strategies);
        } catch (err) {
        }
      };
      fetchCurated();
    }
  }, [curatedStrategies.length, showCuratedStrategies]);

  // Fetch curated sessions count on mount, and full list when curated tab is active
  useEffect(() => {
    const fetchCuratedSessions = async () => {
      try {
        setIsCuratedFetching(true);
        const params: { difficulty?: string; theme?: string } = {};
        if (selectedDifficulty) params.difficulty = selectedDifficulty;
        if (selectedTheme) params.theme = selectedTheme;
        const res = await apiClient.getCuratedSessions(params);
        startTransition(() => {
          setCuratedSessions(res.sessions);
          setCuratedCount(res.total);
          setHasLoadedCuratedSessions(true);
        });
      } catch (err) {
        setHasLoadedCuratedSessions(true);
      } finally {
        setIsCuratedFetching(false);
      }
    };
    fetchCuratedSessions();
  }, [selectedDifficulty, selectedTheme, startTransition]);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        if (isInitialLoad) {
          setLoading(true);
        } else {
          setIsFetching(true);
        }

        const currentLimit = variant === "teaser" ? limit : fetchLimit;

        const response = await apiClient.getTopSessions({
          limit: currentLimit,
          min_score: minScore,
          ...(sortBy === "recent" && { sort_by: "recent" }),
          ...(selectedCategory && { category: selectedCategory }),
        });
        setSessions(response.sessions);
        setTotal(response.total ?? null);

        if (variant === "full") {
          const totalFromApi = response.total;
          if (typeof totalFromApi === "number") {
            setHasMore(response.sessions.length < totalFromApi);
          } else {
            setHasMore(response.sessions.length >= currentLimit);
          }
        } else {
          setHasMore(false);
        }

        setError(null);
      } catch (err) {
        setError("Failed to load feed");
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
        setIsLoadingMore(false);
        setIsFetching(false);
      }
    };

    fetchSessions();
  }, [limit, fetchLimit, minScore, sortBy, variant, selectedCategory]);

  useEffect(() => {
    setFetchLimit(limit);
  }, [limit, sortBy, minScore, selectedCategory]);

  return (
    <div className={cn("w-full max-w-6xl mx-auto px-4", variant === "teaser" ? "pt-0 pb-0" : "py-2")}>
      {/* ═══ DISCOVER SECTION ═══ */}
        <section
          data-tour="home-content-feed"
          className="rounded-3xl border border-white/10 bg-black/20 p-3 sm:p-4 shadow-[0_18px_60px_rgba(0,0,0,0.28)]"
        >
          <FeedHeader
            totalUsers={totalUsers}
            stickyHeader={stickyHeader}
            variant={variant}
            sortBy={sortBy}
            onSortChange={setSortBy}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            tourActiveTab={tourActiveTab}
          />

          {/* Animated light beam progress bar */}
          <div className="relative h-[1px] w-full mb-2 overflow-hidden">
            {isFetching && (
              <div className="absolute inset-0 bg-zinc-800">
                <div
                  className="h-full w-1/3 bg-gradient-to-r from-transparent via-white to-transparent animate-sweep"
                  style={{ animationDirection: "alternate" }}
                />
              </div>
            )}
          </div>

          <div className="relative">
            {/* ── Teaser: tab-driven content ── */}
            {variant === "teaser" ? (
              <>
                <div
                  className="flex gap-4 overflow-x-auto pb-2 no-scrollbar scroll-smooth snap-x snap-proximity"
                >
                  {activeTab === "skills" && (
                    <>
                      {skillItems.map((item) => (
                        <div key={item.id} className="min-w-[320px] w-[320px] sm:min-w-[360px] sm:w-[360px] h-[288px] shrink-0">
                          <LibraryCard
                            item={item}
                            onSelect={() => router.push(`/skills/${item.id}`)}
                          />
                        </div>
                      ))}
                    </>
                  )}

                  {activeTab === "signals" && (
                    <>
                      {signalItems.map((item) => (
                        <div key={item.id} className="min-w-[320px] w-[320px] sm:min-w-[360px] sm:w-[360px] h-[288px] shrink-0">
                          <LibraryCard
                            item={item}
                            onSelect={() => router.push(`/alerts/${item.id}`)}
                          />
                        </div>
                      ))}
                    </>
                  )}

                  {activeTab === "watch" && (
                    <>
                      {LIBRARY_VIDEOS.map((item) => (
                        <div key={item.id} className="min-w-[320px] w-[320px] sm:min-w-[360px] sm:w-[360px] h-[288px] shrink-0">
                          <LibraryCard
                            item={item}
                            onSelect={() => {
                              if (!item.comingSoon && item.youtubeUrl) {
                                setActiveVideo(item);
                              }
                            }}
                          />
                        </div>
                      ))}
                    </>
                  )}

                  {activeTab === "curated" && (
                    <>
                      {CASE_STUDIES.map((study) => (
                        <div key={study.slug} className="min-w-[320px] w-[320px] sm:min-w-[360px] sm:w-[360px] h-[288px] shrink-0">
                          <CaseStudyCard study={study} compact className="h-full" />
                        </div>
                      ))}
                    </>
                  )}

                  {activeTab === "community" && (
                    <>
                      {sessions.length === 0
                        ? Array.from({ length: 4 }).map((_, i) => (
                            <div
                              key={`skeleton-community-${i}`}
                              className="min-w-[320px] w-[320px] sm:min-w-[360px] sm:w-[360px] h-[288px] shrink-0 animate-[fadeIn_500ms_ease-out_both]"
                              style={{ animationDelay: `${80 + i * 60}ms` }}
                            >
                              <FeedCardSkeleton framed />
                            </div>
                          ))
                        : sessions.slice(0, 4).map((session) => (
                            <div key={session.session_id} className="min-w-[320px] w-[320px] sm:min-w-[360px] sm:w-[360px] h-[288px] shrink-0">
                              <FeedCard session={session} framed />
                            </div>
                          ))}
                    </>
                  )}

                  {activeTab === "automations" && (() => {
                    // One-line decision trace every render: tells us exactly
                    // which branch got picked in `frontend_autopilot.txt`.
                    const _branch =
                      featuredLoading && featuredAlerts.length === 0
                        ? "skeletons"
                        : featuredAlerts.length > 0
                          ? `cards(${featuredAlerts.length})`
                          : "empty";
                    autopilotLog("render:automations_tab", {
                      branch: _branch,
                      featuredLoading,
                      featuredLoaded,
                      featuredAlertsLen: featuredAlerts.length,
                    });
                    return (
                      <>
                        {/* Featured creator alerts wall. Skeletons while the
                         *  first fetch is in flight so the row occupies its
                         *  final height with no layout shift. */}
                        {featuredLoading && featuredAlerts.length === 0
                          ? Array.from({ length: 4 }).map((_, i) => (
                              <div key={`featured-skeleton-${i}`} className="min-w-[320px] w-[320px] sm:min-w-[360px] sm:w-[360px] h-[288px] shrink-0">
                                <AutopilotCreatorCardSkeleton className="h-full w-full" />
                              </div>
                            ))
                          : featuredAlerts.map((item, i) => (
                              <div
                                key={item.alert_id}
                                className="min-w-[320px] w-[320px] sm:min-w-[360px] sm:w-[360px] h-[288px] shrink-0 animate-[fadeIn_400ms_ease-out_both]"
                                style={{ animationDelay: `${60 + i * 40}ms` }}
                              >
                                <AutopilotCreatorCard
                                  item={item}
                                  className="h-full w-full"
                                />
                              </div>
                            ))}
                      </>
                    );
                  })()}
                </div>

                <div className="flex justify-center pt-3 pb-2">
                  <Link
                    href={
                      activeTab === "community"
                        ? tourActiveTab === "community"
                          ? buildCommunityTourUrl()
                          : "/community"
                        : buildLibraryBrowseMoreHref(activeTab)
                    }
                    data-tour="home-automations-browse-more"
                    passHref
                  >
                    <Button
                      variant="outline"
                      className="bg-zinc-900/80 border-zinc-700 text-white hover:bg-zinc-800 hover:text-white px-6 py-5 rounded-xl text-sm group"
                    >
                      Browse more
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              /* ── Full mode: existing grid layout ── */
              <>
                {sessions.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-zinc-400">No sessions to display yet</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-12">
                      {/* Render Curated Strategies (Pinned) */}
                      {showCuratedStrategies && curatedStrategies.map((strategy) => (
                        <CuratedCard key={strategy.id} strategy={strategy} />
                      ))}

                      {sessions.map((session, idx) => (
                        <div key={session.session_id}>
                          <FeedCard
                            session={session}
                            tourActive={communityTourActive}
                            tourTarget={communityTourActive && idx === 0}
                          />
                        </div>
                      ))}
                    </div>

                    {hasMore && (
                      <div className="flex items-center justify-center mt-6">
                        <button
                          onClick={() => {
                            setIsLoadingMore(true);
                            setFetchLimit((prev) => prev + limit);
                          }}
                          disabled={isLoadingMore}
                          className="px-4 py-2 text-sm font-medium rounded-lg transition-all bg-zinc-800 text-zinc-200 hover:text-white hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isLoadingMore ? "Loading..." : "Load more"}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </section>
      <LibraryVideoModal item={activeVideo} onClose={() => setActiveVideo(null)} />
    </div>
  );
}

export const Feed = memo(PureFeed);
